import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_status?: string;
  subscription_end?: string;
}

const SUBSCRIPTION_CACHE_KEY = 'subscription_data';
const SUBSCRIPTION_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const getCachedSubscription = (): SubscriptionData | null => {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > SUBSCRIPTION_CACHE_TTL;
      if (!isExpired && data?.subscribed === true) {
        return data;
      }
      localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
    }
  } catch (error) {
    console.warn('Failed to parse cached subscription:', error);
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
  }
  return null;
};

const setCachedSubscription = (data: SubscriptionData) => {
  try {
    localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to cache subscription data:', error);
  }
};

export const useSubscription = () => {
  const {
    data: subscriptionData,
    isLoading: loading,
    refetch
  } = useQuery<SubscriptionData>({
    queryKey: ['subscription'],
    queryFn: async () => {
      console.log('[useSubscription] Fetching subscription...');
      const { data, error } = await supabase.functions.invoke('check-subscription');
      console.log('[useSubscription] Response:', { data, error });
      
      if (error) {
        console.error('[useSubscription] Error:', error);
        throw error;
      }
      
      if (data) {
        setCachedSubscription(data);
      }
      
      return data;
    },
    initialData: getCachedSubscription() ?? undefined,
    staleTime: SUBSCRIPTION_CACHE_TTL,
    gcTime: SUBSCRIPTION_CACHE_TTL * 2,
    refetchOnWindowFocus: false,
  });

  console.log('[useSubscription] Current state:', { subscriptionData, loading });

  const getTeamMemberLimit = (): number | null => {
    const isSubscribed = subscriptionData?.subscribed && subscriptionData?.subscription_status !== 'canceled';
    
    if (!isSubscribed) {
      return 3; // Free tier
    }

    const tier = subscriptionData.subscription_tier;
    
    if (tier?.includes('Professional') || tier === 'Premium' || tier?.includes('Pro')) {
      return 10; // Professional plan - updated from unlimited to 10
    } else if (tier?.includes('Starter') || tier === 'Basic') {
      return 5; // Starter plan
    }
    
    return 3; // Default to free tier
  };

  return {
    subscriptionData,
    loading,
    getTeamMemberLimit,
    refetch
  };
};